import React from 'react';
import { Info, FileText, MapPin, AlertTriangle, FileCheck, Briefcase, Plane, Globe, Shield } from 'lucide-react';

/**
 * Компонент с информацией о визах и советами по их получению
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-purple-500" />
        Всё о визах: особенности получения для граждан России
      </h2>
      
      <div className="prose prose-purple dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-500" />
          Что такое виза и для чего она нужна?
        </h3>
        <p>
          <strong>Виза</strong> — это документ (или отметка в паспорте), дающий право на въезд в определенную страну, пребывание на ее территории и выезд из страны. Каждая виза имеет свой срок действия, ограничения по продолжительности пребывания и количеству разрешенных въездов.
        </p>
        <p>
          Виза не является гарантией въезда в страну. Окончательное решение о допуске иностранца на территорию государства принимают сотрудники пограничной службы при пересечении границы.
        </p>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-purple-500" />
          Особенности визового режима для россиян в 2025 году
        </h3>
        <p>
          В связи с геополитической ситуацией условия получения виз для граждан России в ряд стран изменились. Важно иметь актуальную информацию перед планированием поездки. Ряд стран приостановил выдачу туристических виз или ввел дополнительные требования к заявителям из России.
        </p>
        <p>
          На данный момент для россиян действует безвизовый режим с более чем 80 странами и территориями, включая большинство стран СНГ, ряд стран Латинской Америки, Юго-Восточной Азии и некоторые другие направления.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Безвизовые страны для россиян</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-2">
            Ряд популярных направлений, куда россияне могут въехать без оформления визы (по состоянию на 2025 год):
          </p>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
            <li>Турция (до 90 дней)</li>
            <li>Таиланд (до 30 дней)</li>
            <li>ОАЭ (до 90 дней)</li>
            <li>Сербия (до 30 дней)</li>
            <li>Марокко (до 90 дней)</li>
            <li>Египет (виза по прибытии)</li>
            <li>Мальдивы (до 30 дней)</li>
            <li>Куба (до 30 дней)</li>
            <li>Армения (до 180 дней)</li>
            <li>Казахстан (до 90 дней)</li>
            <li>Грузия (до 360 дней)</li>
            <li>Азербайджан (до 90 дней)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-indigo-500" />
          Основные типы виз
        </h3>
        <ul>
          <li><strong>Туристическая виза:</strong> Для поездок с туристическими целями. Обычно краткосрочная, с ограниченным сроком пребывания.</li>
          <li><strong>Деловая виза:</strong> Для деловых поездок, переговоров, участия в конференциях, но не для трудоустройства.</li>
          <li><strong>Рабочая виза:</strong> Для официального трудоустройства в стране. Требует наличия приглашения от работодателя.</li>
          <li><strong>Учебная виза:</strong> Для обучения в учебных заведениях страны.</li>
          <li><strong>Транзитная виза:</strong> Для кратковременного пребывания в стране при следовании в третью страну.</li>
          <li><strong>Виза для воссоединения семьи:</strong> Для переезда к родственникам, проживающим в иностранном государстве.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-500" />
          Как выбрать правильный тип визы?
        </h3>
        <p>
          При выборе типа визы учитывайте реальную цель поездки. Нельзя, например, работать по туристической визе или вести бизнес по студенческой — это может привести к депортации и запрету на въезд в страну. Если у вас несколько целей, выбирайте визу по основной цели поездки.
        </p>
        <p>
          Обратите внимание на необходимую продолжительность визы и количество въездов. Если вы планируете несколько поездок в течение года, целесообразно запрашивать многократную визу.
        </p>

        <h3 className="flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-green-500" />
          Документы для получения визы
        </h3>
        <p>
          Базовый пакет документов для большинства виз включает:
        </p>
        <ul>
          <li>Загранпаспорт (срок действия обычно должен превышать срок окончания визы на 3-6 месяцев)</li>
          <li>Анкета-заявление на получение визы</li>
          <li>Цветные фотографии установленного образца</li>
          <li>Подтверждение бронирования жилья</li>
          <li>Подтверждение финансовой состоятельности (выписки с банковских счетов)</li>
          <li>Медицинская страховка, действующая в стране посещения</li>
          <li>Билеты или бронь билетов туда и обратно</li>
          <li>Подтверждение цели поездки (для туристической визы — маршрут; для деловой — приглашение от компании и т.д.)</li>
        </ul>
        <p>
          Специфические требования зависят от страны и типа визы. Для некоторых стран может потребоваться документы, переведенные на официальный язык страны и заверенные нотариально.
        </p>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Особенности визового процесса для россиян
          </h4>
          <ul className="text-amber-600 dark:text-amber-400 text-sm mb-0">
            <li>В 2025 году во многих посольствах и консульствах европейских стран существуют ограничения на выдачу туристических виз россиянам</li>
            <li>Время рассмотрения заявлений увеличилось в среднем до 2-4 недель</li>
            <li>Для подачи документов в некоторые консульства требуется личное присутствие заявителя</li>
            <li>Выросли требования к финансовым гарантиям и дополнительной документации</li>
            <li>Консульские сборы для россиян могут быть выше, чем для граждан других стран</li>
            <li>Многие страны стали чаще выдавать однократные визы вместо многократных</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-teal-500" />
          Как увеличить шансы на получение визы?
        </h3>
        <ol>
          <li><strong>Подготовьте полный пакет документов.</strong> Отсутствие даже одной справки может стать причиной отказа.</li>
          <li><strong>Подтвердите свои прочные связи с Россией.</strong> Наличие недвижимости, постоянной работы, семьи в России демонстрирует, что вы вернетесь на родину.</li>
          <li><strong>Предоставьте детальный маршрут поездки</strong> с указанием всех мест проживания и планируемых активностей.</li>
          <li><strong>Продемонстрируйте достаточные финансовые средства.</strong> На счету должна быть сумма, покрывающая все расходы в поездке (минимум 50-100 евро на день пребывания).</li>
          <li><strong>Предоставьте историю предыдущих поездок</strong>, особенно если у вас уже были визы в эту или другие страны, которые вы посещали и своевременно покидали.</li>
          <li><strong>Будьте честны в анкете и на собеседовании.</strong> Любое несоответствие в предоставленной информации может вызвать подозрения.</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-rose-500" />
          Страховка и другие дополнительные услуги
        </h3>
        <p>
          <strong>Медицинская страховка</strong> — обязательное требование для получения визы большинства стран. Минимальная сумма покрытия обычно составляет 30,000 евро. Важно, чтобы страховка покрывала весь период пребывания в стране. 
        </p>
        <p>
          <strong>Дополнительные услуги визовых центров</strong> в 2025 году включают:
        </p>
        <ul>
          <li>VIP-обслуживание с отдельным приемом и персональным менеджером</li>
          <li>Помощь в заполнении анкет и проверка документов</li>
          <li>Фотографирование, ксерокопирование, перевод документов</li>
          <li>SMS-информирование о готовности визы</li>
          <li>Курьерская доставка готовых документов</li>
          <li>Страхование от отказа в визе (возвращается консульский сбор при отказе)</li>
        </ul>

        <h3>Шенгенская виза для россиян</h3>
        <p>
          Шенгенская виза позволяет свободно перемещаться по территории 27 европейских стран. Для россиян действуют следующие правила:
        </p>
        <ul>
          <li>Консульский сбор для граждан России составляет 80 евро (дети 6-12 лет — 40 евро)</li>
          <li>Максимальный срок пребывания — 90 дней в течение 180-дневного периода</li>
          <li>Анкета заполняется на английском языке или на языке страны, в которую совершается поездка</li>
          <li>При первом получении шенгенской визы обычно требуется сдача биометрических данных (отпечатки пальцев)</li>
          <li>Заявление на визу нужно подавать в консульство страны основного назначения или страны первого въезда</li>
        </ul>

        <p className="font-semibold mt-6 text-purple-600 dark:text-purple-400">
          Наш калькулятор поможет вам рассчитать полную стоимость оформления визы с учетом всех сборов и дополнительных услуг для конкретной страны и типа визы!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;